
import {Vue, Component, Prop} from 'vue-property-decorator';
import ChartNumberDataItem from "@/dto/ChartNumberDataItem";
import axiosInstance from "@/axios";
import IChartIntDataDto from "@/dto/IChartIntDataDto";
import dayjs from "dayjs";

import {use} from "echarts/core";
import {CanvasRenderer} from "echarts/renderers";
import {LineChart} from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  ToolboxComponent,
  GridComponent,
  DataZoomComponent,
  VisualMapComponent,
  MarkLineComponent

} from "echarts/components";
import VChart from "vue-echarts";

use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  ToolboxComponent,
  GridComponent,
  DataZoomComponent,
  VisualMapComponent,
  MarkLineComponent
]);

@Component({
  components: {VChart}
})
export default class AnswerChart extends Vue {
  @Prop() apiKey?: string
  @Prop() channelUserId?: string

  private pageSize = 10;
  private chartData: ChartNumberDataItem[] = [];

  private isLoading = false;
  private isLoadDone = false;
  private hasError = false;
  private errorCode: number | undefined;
  private errorMessage: string | undefined;


  mounted() {
    this.loadData()
  }

  get isDataReady(): boolean {
    return this.isLoadDone && this.chartData.length > 0;
  }

  loadData() {
    if (!this.apiKey || !this.channelUserId) return

    const id = '00000000-0000-0000-0000-000000000000';
    this.isLoading = true;
    axiosInstance.get(`/api/reports/external/${this.apiKey}:${this.channelUserId}:${id}/vas`)
        .then(response => {
          this.chartData = response.data.map((dto: IChartIntDataDto) => {
            return ChartNumberDataItem.of(dto)
          });
          this.isLoadDone = true;
          this.hasError = false;
        })
        .catch(error => {
          this.hasError = true;
          this.buildErrorData(error);
        })
        .finally(() => {
          this.isLoading = false;
        })
  }

  buildErrorData(error: any) {
    this.hasError = true;
    this.errorCode = error?.response?.status;
    if (this.errorCode === 400) {
      this.errorMessage = error?.response?.data.detail;
    } else {
      this.errorMessage = error?.response?.statusText;
    }
  }

  get latestData(): string {
    if (!this.isDataReady) {
      return '-';
    }
    return this.chartData[this.chartData.length - 1]._date.format("DD.MM.YYYY");
  }

  get notNullChartData() {
    return this.chartData.filter(i => i.value != null);
  }

  get latest30DaysData() {
    if (this.notNullChartData.length < 30) {
      return this.notNullChartData;
    }
    return this.notNullChartData.slice(this.notNullChartData.length - 30, this.notNullChartData.length);
  }

  sortByValueAndDate = (data: ChartNumberDataItem[]) => {
    return data.sort((a, b) => {
      // Only sort on value if not identical
      if ((a.value ? a.value : 0) < (b.value ? b.value : 0)) return -1;
      if ((a.value ? a.value : 0) > (b.value ? b.value : 0)) return 1;
      // Sort on name
      if (a._date.isBefore(b._date)) return -1;
      if (a._date.isAfter(b._date)) return 1;
      // Both identical, return 0
      return 0;
    });
  }

  get minValueLatest30days() {
    if (this.latest30DaysData.length === 0) {
      return undefined;
    }
    const latestData = this.sortByValueAndDate([...this.latest30DaysData]);
    const minValue = latestData[0].value;
    const allMinValues = latestData.filter(i => i.value === minValue)
    return allMinValues[allMinValues.length - 1]
  }

  get maxValueLatest30days() {
    if (this.latest30DaysData.length === 0) {
      return undefined;
    }
    const latestData = this.sortByValueAndDate([...this.latest30DaysData]);
    return latestData[latestData.length - 1]
  }

  get buildChatOption(): any {
    const {code} = this.$route.params;
    const reportDate = dayjs().format("DD.MM.YYYY, HHmmss")
    return {
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: 'none',
            title: {
              zoom: 'Выбрать область',
              back: 'Сбросить зум'
            }
          },
          restore: {
            title: 'Восстановить исходное состояние'
          },
          saveAsImage: {
            name: `График ВАШ [${code}], ${reportDate}`,
            title: 'Сохранить график',
            excludeComponents: ['dataZoom', 'toolbox'],
            pixelRatio: 2
          }
        }
      },
      xAxis: {
        type: 'time',
        // boundaryGap: false,
        // scale: true,
        axisLabel: {
          rotate: 0,
          showMinLabel: true,
          showMaxLabel: true,
          formatter: function (value: Date) {
            const date = dayjs(value);
            return date.format('DD.MMM')
          },
          hideOverlap: true,
        },
      },
      yAxis: {
        type: 'value',
        boundaryGap: [0, '30%'],
        max: 10,
        min: 0,
        name: 'Уровень боли по ВАШ',
        nameLocation: 'middle',
        nameGap: 40
      },
      tooltip: {
        trigger: 'axis',
        formatter: function (params: any) {
          const date = dayjs(params[0].data[0]);
          let output = `<div style="text-align: left">`;
          output += `<div style="text-align: right;">${date.format('dd, HH:mm, <b>DD.MM.YYYY</b>')}</div>`;
          // output += `<div  style="text-align: right; font-weight: bold;">${date.format('dddd')}</div>`;
          for (let i = 0; i < params.length; i++) {
            output += `<div style="white-space: nowrap;">${params[i].marker} ${params[i].seriesName}:&nbsp;<div style="display: inline-block; float: right; font-weight: 600;">${params[i].value[1]}</div></div>`;
          }
          output += `</div>`;
          return output
        }

      },
      dataZoom: [
        {
          start: this.notNullChartData.length < 30 ? 0 : 80
        },
        {
          type: 'inside'
        }
      ],
      visualMap: {
        show: false,
        top: 32,
        right: 70,
        type: 'continuous',
        seriesIndex: 0,
        min: 0,
        max: 10,
        text: ['10', '0'],
        inRange: {
          color: ['#0E850E', '#88B50B', '#CEBA08', '#E87D05', '#FF2D04'],
          symbolSize: [6, 18]
        },
        dimension: 1
      },
      series: [
        {
          name: 'ВАШ',
          type: 'line',
          smooth: 0.6,
          symbol: 'circle',
          symbolSize: 6,
          showAllSymbol: false,
          connectNulls: true,
          lineStyle: {
            // color: '#5470C6',
            width: 3
          },
          label: {
            // show: true,
            position: 'top'
          },
          markLine: {
            symbol: ['none', 'none'],
            silent: true,
            animation: false,
            lineStyle: {
              type: 'dashed',
              color: "#cccccc"
            },
            label: {
              show: true,
              position: 'insideEndTop',
              formatter: function (params: any) {
                const date = dayjs(params.value);
                return date.format('MMM, YYYY')
              }
            },
            data: [
              ...this.getMonthLineMarks(this.chartData),
            ]
          },
          areaStyle: {
            opacity: 0.5
          },
          // data: this.chartData.map(({date, value}) => { return [date.toDate(), value]})
          data: this.chartData.map(i => [i.date, i.value])
        }
      ]
    }
  }

  getMonthLineMarks = (data: ChartNumberDataItem[]) => {
    let month = -1;
    const lineMarks: any[] = [];
    data.forEach(item => {
      if (month !== item._date.month()) {
        month = item._date.month();
        lineMarks.push({xAxis: item.date})
      }
    })
    if (lineMarks.length > 1) {
      return lineMarks.splice(1, lineMarks.length - 1);
    }
    return lineMarks;
  }
  getMonthMarks2 = (data: any) => {
    let month = -1;
    let lines: any = [];
    data.forEach((val: any, index: number) => {
      if (month != val[0].getMonth()) {
        month = val[0].getMonth();
        lines.push({name: 'month', xAxis: index})
      }
    })
    return lines.splice(1, lines.length - 2);
  }

  private colors: string[] = [
    '#0E850E',
    '#26910E',
    '#429C0D',
    '#62A90C',
    '#88B50B',
    '#B2C10A',
    '#CEBA08',
    '#DB9E06',
    '#E87D05',
    '#F55702',
    '#FF2D04',
  ];
}
